/* @flow */

import React, { useContext, useEffect, useState } from "react";
import { useTranslate } from "@awardit/react-use-translate";
import { useModal } from "@crossroads/ui-components";
import { StoreInfoContext, useClient } from "entrypoint/shared";
import { getTransactionReceipt } from "queries";
import Spinner from "components/Spinner";
import CloseIcon from "icons/close-small.svg";

import styles from "./styles.scss";

type ReceiptProps = {
  currentReceipt: string | null,
  setCurrentReceipt: (string | null) => void,
};

type ReceiptRow = {
  title: ?string,
  value: ?string,
};

const Receipt = ({ currentReceipt, setCurrentReceipt }: ReceiptProps): React$Node => {
  const client = useClient();
  const t = useTranslate();
  const { info: { locale } } = useContext(StoreInfoContext);
  const [receiptRows, setReceiptRows] = useState<Array<ReceiptRow>>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const close = () => {
    setCurrentReceipt(null);
  };

  const { Modal } = useModal(Boolean(currentReceipt), isOpen => {
    if (!isOpen) {
      close();
    }
  });

  useEffect(() => {
    if (!currentReceipt) {
      setReceiptRows([]);
      setLoading(false);

      return;
    }

    setReceiptRows([]);
    setLoading(true);

    client(getTransactionReceipt, {
      transactionID: currentReceipt,
      langattribute: locale ? locale.slice(0, 2) : undefined,
    }).then(({ getTransactionReceipt }) => {
      setReceiptRows(getTransactionReceipt || []);
      setLoading(false);
    }).catch(e => {
      console.error(e);
      setLoading(false);
    });
  }, [client, currentReceipt, locale]);

  if (!currentReceipt) {
    return null;
  }

  return (
    <Modal className={styles.wrapper}>
      <div className={styles.container}>
        <div className={styles.topSection}>
          <div className={styles.title} id="dialog-title">{t("TRANSACTIONS.RECEIPT")}</div>
          <button className={styles.closeIcon} type="button" aria-label={t("DIALOGUE.CLOSE")} onClick={close}>
            <CloseIcon />
          </button>
        </div>

        {loading &&
          <div className={styles.loading}>
            <Spinner />
          </div>
        }

        {!loading &&
          <>
            <table className={styles.table}>
              <tbody>
                {receiptRows.map((row, index) => (
                  <tr key={`${row.title || ""}-${index}`}>
                    <th>{row.title}</th>
                    <td>{row.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <button
              type="button"
              className={styles.print}
              onClick={e => {
                e.preventDefault();
                window.print();
              }}
            >
              {t("COMMON.PRINT")}
            </button>
          </>
        }
      </div>
    </Modal>
  );
};

export default Receipt;
